import React from 'react';
import * as Sentry from '@sentry/react';
import type { ColumnConfigV3 } from '../compactTableLogic';
import type { FieldKey, ViewColumn } from '../views/types';
import { fieldAccess, visibleColumns, visiblePresets } from './fieldAccess';
import {
  columnFloorWidth,
  columnHeading,
  columnSortField,
  effectiveColumnWidth,
  FieldMeta,
  FieldPreset,
  isIdentityColumnPinned,
  stackRuns,
  Translate,
} from './columnModel';

/** The column a field is drawn in, for a field that looks different depending on its neighbours. */
export interface FieldCell {
  /** The column's fields as the user sees them, this one included. */
  members: FieldKey[];
}

/** A catalogue field: its metadata plus how it draws one row's value. */
export interface FieldDefinition<TRow> extends FieldMeta {
  render: (row: TRow, toggleExpandedRow: ((row: TRow) => void) | undefined, cell: FieldCell) => React.ReactNode;
}

/**
 * One field's value in a cell. Guarded on its own: a field whose data the payload no longer carries
 * (the backend evolved) or that throws for one odd row renders empty and is reported, instead of
 * taking the whole table down with it.
 */
const FieldValue = <TRow,>({
  field,
  row,
  toggleExpandedRow,
  cell,
}: {
  field: FieldDefinition<TRow>;
  row: TRow;
  toggleExpandedRow?: (row: TRow) => void;
  cell: FieldCell;
}) => (
  <Sentry.ErrorBoundary fallback={<></>} beforeCapture={scope => scope.setTag('tableField', field.key)}>
    {field.render(row, toggleExpandedRow, cell)}
  </Sentry.ErrorBoundary>
);

/**
 * A stacked cell: members top to bottom in the order the user put them, each keeping its own
 * component, and neighbouring tags flowing in one wrapping row. A single member renders exactly as it
 * would on its own, so grouping a field never changes how it looks — only where it sits.
 */
const StackedCell = <TRow,>({
  members,
  fields,
  row,
  toggleExpandedRow,
}: {
  members: FieldKey[];
  fields: Map<FieldKey, FieldDefinition<TRow>>;
  row: TRow;
  toggleExpandedRow?: (row: TRow) => void;
}) => {
  const runs = stackRuns(members, fields);
  const cell: FieldCell = { members };
  if (runs.length === 1 && runs[0].kind === 'block') {
    const field = fields.get(runs[0].key);
    return field ? <FieldValue field={field} row={row} toggleExpandedRow={toggleExpandedRow} cell={cell} /> : null;
  }
  return (
    <div className="flex flex-col items-start gap-1">
      {runs.map(run =>
        run.kind === 'block' ? (
          fields.get(run.key) && <FieldValue key={run.key} field={fields.get(run.key)} row={row} toggleExpandedRow={toggleExpandedRow} cell={cell} />
        ) : (
          <div key={run.keys.join('|')} className="flex flex-wrap items-start gap-2">
            {run.keys.map(key => fields.get(key) && <FieldValue key={key} field={fields.get(key)} row={row} toggleExpandedRow={toggleExpandedRow} cell={cell} />)}
          </div>
        ),
      )}
    </div>
  );
};

/**
 * A view's columns as the table's column config. Built fresh on every render, so cells always close
 * over the page's current state (expanded tags, flags) — never freeze these in state.
 */
export const buildColumnConfig = <TRow,>(args: {
  columns: ViewColumn[];
  catalogue: FieldDefinition<TRow>[];
  presets: FieldPreset[];
  tr: Translate;
  /** Columns that are not configurable and always close the table — the row actions. */
  trailing?: ColumnConfigV3<TRow>[];
}): ColumnConfigV3<TRow>[] => {
  const fields = new Map(args.catalogue.map(field => [field.key, field]));
  // Only what this user may see: unknown members (skipped at render too, not only at load) and
  // fields gated by a permission they lack. Heading, width and sort follow from what is left.
  const isAllowed = fieldAccess(args.catalogue);
  const presets = visiblePresets(args.presets, isAllowed);
  const configured = visibleColumns(args.columns, isAllowed).map<ColumnConfigV3<TRow>>((column, index) => {
    // The user's own width wins (kept above what its components need); otherwise the width the members (or the preset) need.
    const minWidth = `${effectiveColumnWidth(column, fields, presets)}px`;
    const sortField = columnSortField(column, fields);
    return {
      id: column.id,
      field: sortField,
      headerText: columnHeading(column, fields, presets, args.tr),
      width: minWidth,
      sortable: !!sortField,
      resizable: true,
      minResizeWidth: columnFloorWidth(column, fields),
      order: index,
      sticky: isIdentityColumnPinned(column, index, fields) ? 'left' : undefined,
      render: (row, toggleExpandedRow) => <StackedCell members={column.members} fields={fields} row={row} toggleExpandedRow={toggleExpandedRow} />,
    };
  });
  // The last configured column takes the width the others leave, as in the Figma; its own width is
  // then just its minimum. All others keep their width, so long text wraps instead of widening them.
  // A column the user sized keeps exactly that width.
  const last = args.columns.length > 0 ? visibleColumns(args.columns, isAllowed).slice(-1)[0] : undefined;
  if (configured.length > 0 && !last?.width) configured[configured.length - 1] = { ...configured[configured.length - 1], fill: true };
  const trailing = (args.trailing ?? []).map((column, i) => ({ ...column, order: configured.length + i }));
  return [...configured, ...trailing];
};
